import React, { useEffect } from 'react';
import Popover from 'components/Popover';
import { Translator } from 'Translator';
import KeywordSearch from 'modules/common/Navbar/popovers/MoreFiltersPopover/KeywordSearch';
import TopFilters from './TopFilters';
import FiltersBlock from 'modules/common/Navbar/popovers/MoreFiltersPopover/FiltersBlock';
import { getMoreFilters, mapSubcategoryToActivities, mapSubcategoryToTours } from 'data/filters';
import MoreFiltersConnector, { MoreFiltersConnectorWrappedProps } from 'modules/common/Navbar/popovers/MoreFiltersPopover/MoreFiltersConnector';

const CSS = require('./MoreFiltersPopover.less');

const isCategorySelected = (selectedFilters, category: 'tours' | 'activities') => {
  const activityType = selectedFilters.find(filter => filter.uid === 'activityType.activity');
  return !!activityType && activityType.subOptions.some(subOption => subOption.value === category);
};

const getSelectedToursSubOptions = (selectedFilters, categoryMap) => {
  const subCategoryKeys = Object.keys(categoryMap);
  const subCategories = selectedFilters.find(filter => filter.uid === 'subcategory.items[]') || { subOptions: [] };
  return subCategories.subOptions.filter(subOption => subCategoryKeys.includes(subOption.value));
};

const getSelectedSubOptions = (selectedFilters, filterName) => {
  const transportModeFilter = selectedFilters.find(filter => filter.uid === filterName);
  if (transportModeFilter) {
    return transportModeFilter.subOptions;
  }
  return [];
};

function MoreFiltersPopover(props: MoreFiltersConnectorWrappedProps) {
  const {
    selectedFilters,
    onApply,
    onKeywordAdd,
    onKeywordRemove,
    onFilterToggle,
    onToursToggle,
    onToursSubcategoryToggle,
    onActivitiesToggle,
    onActivitiesSubcategoryToggle,
    onTransportModeSubOptionToggle,
    onPriceSubOptionToggle,
    onDurationSubOptionToggle,
    onClear,
    amountOfResults,
    resultsLoading,
    aggregations,
  } = props;

  const keywordsFilter = selectedFilters.find(filter => filter.uid === 'keywords');
  const keywords = keywordsFilter ? keywordsFilter.value.split(' ') : '';
  const filters = getMoreFilters(aggregations);

  useEffect(() => {
    function onKeyDown(e) {
      // @ts-ignore
      if (e?.key === 'Enter' && !(document.activeElement?.value ?? '')) {
        onApply('enter button')(e);
      }
    }

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <Popover show name="moreFilterPopover" underNavbar className={CSS.Popover} onClose={onApply('escape button')} onOutSideClick={onApply('outside click')}>
      <div className={CSS.MoreFilters}>
        <div className={CSS.MoreFiltersContent}>
          <div className={CSS.TopRow}>
            <KeywordSearch
              keywords={keywords}
              onKeywordAdd={onKeywordAdd}
              onKeywordRemove={onKeywordRemove}
            />
            <TopFilters
              selectedCategories={selectedFilters}
              onCategoryToggle={onFilterToggle}
            />
          </div>
          <div className={CSS.CategoriesContainer}>
            {filters &&
              <>
                {filters.toursSubcategoriesFilters.length > 0 &&
                  <FiltersBlock
                    onMainFilterChange={onToursToggle}
                    onSubFilterChange={onToursSubcategoryToggle}
                    mainFilter={filters.toursFilter}
                    subFilters={filters.toursSubcategoriesFilters}
                    isMainFilterSelected={isCategorySelected(selectedFilters, 'tours')}
                    selectedSubFilters={getSelectedToursSubOptions(selectedFilters, mapSubcategoryToTours)}
                  />
                }
                {filters.activitiesSubcategoriesFilters.length > 0 &&
                  <FiltersBlock
                    onMainFilterChange={onActivitiesToggle}
                    onSubFilterChange={onActivitiesSubcategoryToggle}
                    mainFilter={filters.activitiesFilter}
                    subFilters={filters.activitiesSubcategoriesFilters}
                    isMainFilterSelected={isCategorySelected(selectedFilters, 'activities')}
                    selectedSubFilters={getSelectedToursSubOptions(selectedFilters, mapSubcategoryToActivities)}
                  />
                }
                <FiltersBlock
                  mainFilter={filters.transportMode}
                  subFilters={filters.transportModeSubOptions}
                  onSubFilterChange={onTransportModeSubOptionToggle}
                  selectedSubFilters={getSelectedSubOptions(selectedFilters, 'other.transport_mode.items[]')}
                />
                <div>
                  <FiltersBlock
                    mainFilter={filters.priceFilter}
                    subFilters={filters.priceSubOptions}
                    onSubFilterChange={onPriceSubOptionToggle}
                    selectedSubFilters={getSelectedSubOptions(selectedFilters, 'price.items[]')}
                  />
                  <div className={CSS.durationFilters}>
                    <FiltersBlock
                      mainFilter={filters.durationFilter}
                      subFilters={filters.durationSubOptions}
                      onSubFilterChange={onDurationSubOptionToggle}
                      selectedSubFilters={getSelectedSubOptions(selectedFilters, 'duration.items[]')}
                    />
                  </div>
                </div>
              </>
            }
          </div>
        </div>
      </div>
      <div className={CSS.ButtonContainer}>
        <button className={CSS.ClearButton} onClick={onClear}>
          {Translator.translate('Common.Clear', {}, true, 'Clear')}
        </button>
        <span className={CSS.AmountText}>
          {Translator.translate('UserEnvironment.Filters.apply',
            { amount: resultsLoading ? '' : amountOfResults }, true, 'Explore {amount} tours and activities')}
          <span className={CSS.AmountDivider}>|</span>
        </span>
        <button className={CSS.ApplyButton} onClick={onApply('apply button')}>
          {Translator.translate('Common.Apply', {}, true, 'Apply')}
        </button>
      </div>
    </Popover>
  );
}

export default MoreFiltersConnector()(MoreFiltersPopover);
